import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import StepProgressBar from "../components/StepProgressBar";
import NegotiationStep1 from "../Create/NegotiationStep1";
import NegotiationStep2 from "../Create/NegotiationStep2";
import NegotiationStep3 from "../Create/NegotiationStep3";
import NegotiationStep4 from "../Create/NegotiationStep4";
import NegotiationStep5 from "../Create/NegotiationStep5";
import NegotiationStep6 from "../Create/NegotiationStep6";

import { negotiationDetails } from "../../../../services/CommonService";
import "../Negotiation.css";

const TOTAL_STEPS = 6;

const NegotiationCreateLayout = () => {
  const { uuid, id } = useParams();
  const navigate = useNavigate();

  const initialForm = {
    // Step 1 form fields
    negotiation_name: "",
    due_date: null,
    contract_type: "",
    contract_term: "Definite",
    start_date: null,
    end_date: null,
    termination_notice_period: "",
    is_renewable: "",
    purpose_of_disclosure: "",
    duration_in_months: "",
    confidential_info_definition: "",
    exclusions: "",
    jurisdiction: "",

    // Other form fields
    current_step: Number(id ?? 1),
    negotiation_id: 0,
    negotiation_uuid: "",
    sender_id: 0,
    template_id: null,
    terms: [],
    documents: [],
    signers: [],
    is_done: false,
    template_uuid: "",
    answers: [],
    html_content: "",
    recipient_list: [],
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formObj, setFormObj] = useState(initialForm);

  // Sync current step if `id` param changes
  useEffect(() => {
    const step = Number(id ?? 1);
    if (!isNaN(step) && step !== formObj.current_step) {
      setFormObj(prev => ({ ...prev, current_step: step }));
    }
  }, [id]);

  // Fetch negotiation details if uuid exists
  useEffect(() => {
    let mounted = true;

    const fetchNegotiationDetails = async () => {
      if (!uuid) {
        if (!mounted) return;
        setFormObj(prev => ({
          ...initialForm,
          current_step: Number(id ?? 1),
        }));
        setLoading(false);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await negotiationDetails({ uuid });
        const payload = response?.data?.data ?? response?.data ?? {};

        if (!mounted) return;

        const d = payload.data ?? payload;

        if (!(d && (d.id || d.uuid))) {
          toast.error("Negotiation data not found");
          navigate("/client-portal/negotiation-contract/create");
          return;
        }

        setFormObj(prev => ({
          ...prev,
          current_step: Number(id ?? prev.current_step ?? 1),
          negotiation_id: d.id || prev.negotiation_id,
          negotiation_uuid: d.uuid || prev.negotiation_uuid,
          template_uuid: d.template_uuid || prev.template_uuid,
          name: d.negotiation_name || prev.name,
          due_date: d.due_date ? new Date(d.due_date) : prev.due_date,
          sender_id: d.sender_id ? Number(d.sender_id) : prev.sender_id,
          template_id: d.template_id ?? prev.template_id,
          terms: d.terms || prev.terms,
          documents: d.documents || prev.documents,
          signers: d.signers || prev.signers,
          is_done: Boolean(d.is_done),
          answers: d.answers || prev.answers,
          html_content: d.html_content || prev.html_content,
          recipient_list: d.recipient_list || prev.recipient_list,

          // Step 1 fields safely updated if present
          negotiation_name: d.negotiation_name || prev.negotiation_name,
          due_date: d.due_date ? new Date(d.due_date) : prev.due_date,
          contract_type: d.contract_type ?? prev.contract_type,
          contract_term: d.contract_term ?? prev.contract_term,
          start_date: d.start_date ? new Date(d.start_date) : prev.start_date,
          end_date: d.end_date ? new Date(d.end_date) : prev.end_date,
          termination_notice_period: d.termination_notice_period ?? prev.termination_notice_period,
          is_renewable: d.is_renewable ?? prev.is_renewable,
          purpose_of_disclosure: d.purpose_of_disclosure ?? prev.purpose_of_disclosure,
          duration_in_months: d.duration_in_months ?? prev.duration_in_months,
          confidential_info_definition: d.confidential_info_definition ?? prev.confidential_info_definition,
          exclusions: d.exclusions ?? prev.exclusions,
          jurisdiction: d.jurisdiction ?? prev.jurisdiction,
        }));
      } catch (err) {
        if (!mounted) return;
        const message = err?.message || "Failed to load negotiation details";
        setError(message);
        toast.error(message);
        navigate("/client-portal/negotiation-contract/create");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchNegotiationDetails();
    return () => {
      mounted = false;
    };
  }, [uuid, id, navigate]);

  // Navigation helpers
  const goToStep = (step) => {
    const clampedStep = Math.min(Math.max(step, 1), TOTAL_STEPS);
    if (formObj.negotiation_uuid) {
      navigate(`/client-portal/negotiation-contract/create/${formObj.negotiation_uuid}/${clampedStep}`);
    } else {
      if (clampedStep > 1) toast.error("Please complete Step 1 first");
      else navigate("/client-portal/negotiation-contract/create");
    }
  };

  const goToNextStep = () => goToStep(formObj.current_step + 1);
  const goToPrevStep = () => goToStep(formObj.current_step - 1);

  // Dynamically render current step component
  const renderStep = () => {
    const commonProps = {
      formObj,
      setFormObj,
      setLoading,
      nextStep: goToNextStep,
      prevStep: goToPrevStep,
    };

    switch (formObj.current_step) {
      case 1:
        return <NegotiationStep1 {...commonProps} />;
      case 2:
        return <NegotiationStep2 {...commonProps} />;
      case 3:
        return <NegotiationStep3 {...commonProps} />;
      case 4:
        return <NegotiationStep4 {...commonProps} />;
      case 5:
        return <NegotiationStep5 {...commonProps} />;
      case 6:
        return <NegotiationStep6 {...commonProps} html_content={formObj.html_content} />;
      default:
        return (
          <div className="error-container">
            <h3>Invalid step</h3>
            <button onClick={() => navigate("/client-portal/negotiation-contract/create")}>Go Back</button>
          </div>
        );
    }
  };

  // Render error state if any
  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => navigate("/client-portal/negotiation-contract/create")}>Try Again</button>
      </div>
    );
  }

  // Main render
  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}

      <div className="negotiation-create-layout section-bg-color">
        {/* <StepProgressBar currentStep={formObj.current_step} totalSteps={TOTAL_STEPS} /> */}
        <div className="step-content-wrapper">{renderStep()}</div>
      </div>
    </>
  );
};

export default NegotiationCreateLayout;
